import moment from "moment";
import {
  GoogleMap,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import { toast } from "sonner";
import api from "../../API/api";
import Select from "react-select";
import MDBox from "components/MDBox";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import colors from "../../assets/theme/base/colors";
import { Box, Button, InputAdornment, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { DataGrid } from "@mui/x-data-grid";
import AddSiteModal from "components/Modals/AddSiteModal/AddSiteModal";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DeleteSiteModal from "components/Modals/DeleteSiteModal/DeleteSiteModal";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MDInput from "components/MDInput";
import { Search } from "@mui/icons-material";

const Dashboard = () => {
  const navigate = useNavigate();
  const { white, gradients } = colors;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [siteData, setSiteData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [addSiteOpen, setAddSiteOpen] = useState(false);
  const [filteredSites, setFilteredSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const [mapCenter, setMapCenter] = useState({
    lat: 20.593684,
    lng: 78.96288,
  });

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setAddSiteOpen(true);
  };

  const handleAddSite = () => {
    setSelectedSite(null);
    setAddSiteOpen(true);
  };

  const handleDeleteSite = async () => {
    if (!siteToDelete) return;

    try {
      let payload = { site_id: siteToDelete?.site_id || siteToDelete?.id };
      const response = await api.delete(`/cv/siteapi`, { data: payload });
      toast.success(response.data.message);
      setDeleteModalOpen(false);
      getAllSites();
    } catch (error) {
      console.error("Error deleting site:", error);
      toast.error("Failed to delete site.");
    }
  };

  const columns = [
    { field: "location", headerName: "Site", width: 220 },
    { field: "description", headerName: "Description", minWidth: 200, flex: 2 },
    { field: "site_name", headerName: "Site Name", minWidth: 170, flex: 1 },
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            color: params.value ? "#1e9b1e" : "#e63535",
            fontWeight: 500,
            fontSize: "16px",
          }}
        >
          {params.value ? "Active" : "Inactive"}
        </span>
      ),
    },
    { field: "creator", headerName: "Creator", minWidth: 150, flex: 1 },
    { field: "created_at", headerName: "Date", minWidth: 150, flex: 1 },
    user?.role === "admin" && {
      field: "actions",
      headerName: "Actions",
      minWidth: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const handleEdit = (e) => {
          e.stopPropagation();
          handleEditSite(params.row);
        };
        const handleDelete = (e) => {
          e.stopPropagation();
          setSiteToDelete(params.row);
          setDeleteModalOpen(true);
        };

        return (
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <IconButton onClick={handleEdit} size="small" color="gray">
              <EditOutlinedIcon />
            </IconButton>
            <IconButton onClick={handleDelete} size="small" color="error">
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleSelectChange = (selectedLocation) => {
    setSelectedOption(selectedLocation);

    if (selectedLocation) {
      const filtered = siteData.filter(
        (site) =>
          site?.location?.trim().toLowerCase() ===
          selectedLocation?.value?.trim().toLowerCase()
      );
      setFilteredSites(filtered);

      const selectedSite = filtered[0];
      if (selectedSite?.latitude && selectedSite?.longitude) {
        setMapCenter({
          lat: parseFloat(selectedSite.latitude),
          lng: parseFloat(selectedSite.longitude),
        });
      }
    } else {
      setFilteredSites(siteData);
      setMapCenter({
        lat: 20.593684,
        lng: 78.96288,
      });
    }
  };
  const getAllSites = async () => {
    try {
      setLoading(true);
      const response = await api.get("/cv/siteapi");
      setSiteData(response?.data?.data);
      setFilteredSites(response?.data?.data);
    } catch (error) {
      console.log(error, "errorr");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllSites();
  }, []);

  const filteredData = siteData
    ?.filter((item) => {
      return (
        item?.site_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .map((item, index) => ({
      id: item?.site_id || "-",
      location: item?.location || "-",
      creator: item?.creator || "-",
      site_name: item?.site_name || "-",
      description: item?.description || "-",
      status: item?.is_active,
      created_at: item?.created_at
        ? moment(item?.created_at).format("DD-MM-YYYY")
        : "-",
    }));

  const mapContainerStyle = {
    width: "100%",
    height: "450px",
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const locationOptions = siteData?.map((site) => ({
    value: site?.location,
    label: site?.location,
  }));

  const KebabMenu = ({ onEdit, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <>
        <IconButton onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              onEdit();
            }}
          >
            <EditOutlinedIcon sx={{ mr: 2 }} />
            Edit
          </MenuItem>
          <MenuItem
            sx={{ color: "error.main", "&:hover": { color: "error.main" } }}
            onClick={() => {
              handleClose();
              onDelete();
            }}
          >
            <DeleteOutlineOutlinedIcon sx={{ mr: 2, color: "error.main" }} />
            Delete
          </MenuItem>
        </Menu>
      </>
    );
  };
  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        fontSize: "20px",
        fontWeight: 500,
        color: "#666",
        opacity: "0.4",
      }}
    >
      No Data Found
    </Box>
  );

  return isLoaded ? (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} sx={{ height: "100vh" }}>
        <Box sx={{ display: "flex", justifyContent: "end", mb: 2 }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              width: "100%",
              justifyContent: "end",
            }}
            className="location-select-box"
          >
            <Select
              defaultValue={selectedOption}
              onChange={handleSelectChange}
              options={locationOptions}
              className="react-select"
              isClearable
              placeholder="Select Location"
            />
            {user?.role === "admin" || user?.role === "user" ? (
              <Button
                variant="info"
                sx={{
                  width: "140px",
                  height: "38px",
                  fontSize: "14px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  background: `${gradients.info.main}`,
                  color: white.main,
                  border: "none",
                  marginRight: "0",
                  marginBottom: "0",
                  "&:hover": {
                    background: `${gradients.info.state} !important`,
                    color: white.main,
                  },
                }}
                onClick={handleAddSite}
                className="add-site-btn"
              >
                Add Site
              </Button>
            ) : (
              ""
            )}
          </Box>
        </Box>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={2}
          center={mapCenter}
          options={{
            disableDefaultUI: false,
            draggable: true,
          }}
        >
          {filteredSites?.map((site, index) => {
            if (!site.latitude || !site.longitude) return null;
            const isActive = site?.is_active === true;
            return (
              <Marker
                key={index}
                position={{
                  lat: parseFloat(site.latitude),
                  lng: parseFloat(site.longitude),
                }}
                icon={{
                  url: isActive
                    ? "/filled-green-marker.png"
                    : "/filled-red-marker.png",
                  scaledSize: new window.google.maps.Size(48, 45),
                }}
              >
                <OverlayView
                  position={{
                    lat: parseFloat(site.latitude),
                    lng: parseFloat(site.longitude),
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "10px",
                      borderRadius: "10px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      width: "200px",
                      textAlign: "center",
                      position: "absolute",
                      right: "0",
                      left: "-104px",
                      top: "-5px",
                      opacity: "0.9",
                    }}
                    className="popover-div"
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "5px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "16px",
                          "&::first-letter": {
                            textTransform: "uppercase",
                          },
                        }}
                      >
                        {site?.location}
                      </Typography>
                      {user?.role === "admin" && (
                        <KebabMenu
                          onEdit={() => handleEditSite(site)}
                          onDelete={() => {
                            setSiteToDelete(site);
                            setDeleteModalOpen(true);
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      style={{
                        color: "#6c757d",
                        fontSize: "14px",
                        display: "flex",
                        marginBottom: "10px",
                      }}
                    >
                      {site.description || "No Description"}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "start" }}>
                      <Button
                        className="map-tag-lable-btn"
                        size="small"
                        sx={{
                          backgroundColor: "#063863",
                          color: "#fff",
                          border: "none",
                          borderRadius: "5px",
                          padding: "0px 5px",
                          fontSize: "9px",
                          cursor: "pointer",
                          fontWeight: "400",
                          width: "186px",
                          "&:hover": {
                            backgroundColor: "#063863",
                            color: "#fff",
                          },
                        }}
                        onClick={() => navigate(`/scenarios/${site?.site_id}`)}
                      >
                        Site
                      </Button>
                    </Box>
                  </div>
                </OverlayView>
              </Marker>
            );
          })}
        </GoogleMap>

        <Box sx={{ pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "25px",
            }}
          >
            <Typography variant="h5" sx={{ pl: 3 }}>
              SITE LIST
            </Typography>
            <Box
              sx={{ display: "flex", justifyContent: "end" }}
              className="search-box"
            >
              <MDInput
                className="search-input"
                placeholder="Search here..."
                value={searchQuery}
                autoComplete="off"
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: "20px !important" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: "300px",
                  marginTop: "8px",
                  paddingLeft: "0",
                  border: "1px solid #9f9a9a",
                  borderRadius: "5px",
                  "& .MuiOutlinedInput-root": {
                    "&:focus, &:focus-within": {
                      outline: "none !important",
                      border: "none !important",
                      boxShadow: "none !important",
                    },
                  },
                  "& .MuiInputBase-input": {
                    "&:focus": {
                      outline: "none !important",
                      border: "none !important",
                      boxShadow: "none !important",
                    },
                  },
                  "& fieldset": {
                    border: "none !important",
                  },
                }}
              />
            </Box>
          </Box>

          <DataGrid
            rowHeight={60}
            loading={loading}
            columns={columns}
            rows={filteredData}
            disableRowSelectionOnClick
            rowCount={siteData.length}
            paginationMode="client"
            pageSizeOptions={[10, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            slots={{
              noRowsOverlay: NoRowsOverlay,
            }}
            paginationModel={{ page: page, pageSize: pageSize }}
            onRowClick={(params) => navigate(`/scenarios/${params.id}`)}
            hideFooter={siteData.length < 10}
            sx={{
              mt: 2,
              fontSize: "15px",
              borderRadius: "8px",
              "& .MuiDataGrid-overlayWrapperInner": { background: "white" },
              "& .MuiCircularProgress-root": {
                color: `${gradients.info.main}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#fff",
                fontWeight: "500",
              },
              "& .MuiDataGrid-cell": {
                outlineOffset: "none !important",
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader": {
                background: "#063863",
                "& .MuiSvgIcon-root": { color: "#fff !important" },
              },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                transition: "background-color 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgb(25 108 215 / 0.08) !important",
                },
              },
            }}
          />
        </Box>
      </MDBox>
      {addSiteOpen && (
        <AddSiteModal
          addSiteOpen={addSiteOpen}
          setAddSiteOpen={setAddSiteOpen}
          selectedSite={selectedSite}
          getAllSites={getAllSites}
        />
      )}
      {deleteModalOpen && (
        <DeleteSiteModal
          deleteModalOpen={deleteModalOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          handleDeleteSite={handleDeleteSite}
          getAllSites={getAllSites}
        />
      )}
    </DashboardLayout>
  ) : (
    <></>
  );
};

export default Dashboard;
